import Multiselect from 'multiselect-react-dropdown';
import React, {useEffect, useRef, useState} from 'react'
import './ModalWide.css'
import { getCurrentUser } from '../utils/auth'

const modalOptionsCache = {
    protocolsByKey: {},
    musicTypes: null,
    customers: null,
};

const modalOptionsCachePromise = {
    protocolsByKey: {},
    musicTypes: null,
    customers: null,
};

const normalizeProtocolOptions = (protocolsData) => {
    if (!Array.isArray(protocolsData)) {
        return [];
    }

    return protocolsData
        .map((item) => {
            const nestedProtocol = item?.protocol ?? null;
            const id = Number(
                item?.id
                ?? item?.protocol_id
                ?? nestedProtocol?.id
                ?? nestedProtocol?.protocol_id
            );
            const name = String(
                item?.name
                ?? item?.protocol_name
                ?? nestedProtocol?.name
                ?? nestedProtocol?.protocol_name
                ?? ''
            ).trim();

            if (!Number.isInteger(id) || !name) {
                return null;
            }

            return { id, name };
        })
        .filter((protocol) => protocol !== null);
};

const ModalWide = ({closeModal, onSubmit, clientEdit, isNewClient, isReadOnly = false, onOpenTreatmentHistory, onOpenTreatmentNotes}) => {
    const tokenPayload = getCurrentUser() || {};
    const tokenUserType = tokenPayload?.user_type;
    const tokenCustomerId = tokenPayload?.customer;
    const isReadOnlyMode = !!isReadOnly;
    const [customerSelectDisabled, setCustomerSelectDisabled] = useState(false)
    const [clientInfo, setClientInfo] = useState(clientEdit);
    const [allProtocols, setAllProtocols] = useState([]);
    const [allCustomers, setAllCustomers] = useState([]);
    const [allMusicTypes, setAllMusicTypes] = useState([]);
    const [clientProtocols, setClientProtocols] = useState([]);
    const [removedClientProtocols, setRemovedClientProtocols] = useState([]);
    const [addedClientProtocols, setAddedClientProtocols] = useState([]);
    const [selectedPersonalizedProtocolIndex, setSelectedPersonalizedProtocolIndex] = useState(0);
    const [isLoadingModalData, setIsLoadingModalData] = useState(true);
    const [savingData, setSavingData] = useState(false);
    const [errors, setErrors] = useState("")
    const protocolSelectRef = useRef(null);
    const [protocolDropdownLayout, setProtocolDropdownLayout] = useState({
        openUpward: false,
        maxHeight: 220,
    });

    const updateProtocolDropdownLayout = () => {
        if (!protocolSelectRef.current || typeof window === 'undefined') {
            return;
        }

        const rect = protocolSelectRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        const edgeGap = 12;
        const preferredMaxHeight = 220;
        const minimumHeight = 120;
        const availableBelow = Math.max(viewportHeight - rect.bottom - edgeGap, 0);
        const availableAbove = Math.max(rect.top - edgeGap, 0);
        const openUpward = availableBelow < minimumHeight && availableAbove > availableBelow;
        const availableSpace = openUpward ? availableAbove : availableBelow;
        const maxHeight = Math.max(minimumHeight, Math.min(preferredMaxHeight, availableSpace || preferredMaxHeight));

        setProtocolDropdownLayout((previousLayout) => {
            if (previousLayout.openUpward === openUpward && previousLayout.maxHeight === maxHeight) {
                return previousLayout;
            }

            return {
                openUpward,
                maxHeight,
            };
        });
    };

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const syncProtocolDropdownLayout = () => {
            updateProtocolDropdownLayout();
        };

        window.addEventListener('resize', syncProtocolDropdownLayout);
        window.addEventListener('scroll', syncProtocolDropdownLayout, true);

        return () => {
            window.removeEventListener('resize', syncProtocolDropdownLayout);
            window.removeEventListener('scroll', syncProtocolDropdownLayout, true);
        };
    }, []);

    useEffect(() => {
        updateProtocolDropdownLayout();
    }, [isLoadingModalData, allProtocols.length, clientProtocols.length]);

    useEffect(() => {
        if (typeof document === 'undefined') {
            return undefined;
        }

        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;

        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousHtmlOverflow;
        };
    }, []);

    useEffect(() => {
        const requestHeaders = {
            'machine':'2',
            'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
        };

        const fetchJson = async (url) => {
            const response = await fetch(url, {
                method: 'GET',
                headers: requestHeaders,
            });
            if (!response.ok) {
                throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
            }
            return response.json();
        };

        const getCachedProtocolsForCustomer = async (customerId) => {
            if (!Number.isInteger(Number(customerId)) || Number(customerId) <= 0) {
                return [];
            }

            const cacheKey = `customer-protocols-${Number(customerId)}`;
            const protocolUrl = process.env.REACT_APP_API_URL_BASE + '/customer_protocols/customer_list/' + Number(customerId);

            if (modalOptionsCache.protocolsByKey[cacheKey]) {
                return modalOptionsCache.protocolsByKey[cacheKey];
            }

            if (!modalOptionsCachePromise.protocolsByKey[cacheKey]) {
                modalOptionsCachePromise.protocolsByKey[cacheKey] = fetchJson(protocolUrl)
                    .then((data) => {
                        modalOptionsCache.protocolsByKey[cacheKey] = data;
                        return data;
                    })
                    .finally(() => {
                        modalOptionsCachePromise.protocolsByKey[cacheKey] = null;
                    });
            }

            return modalOptionsCachePromise.protocolsByKey[cacheKey];
        };

        const getCachedMusicTypes = async () => {
            if (modalOptionsCache.musicTypes) {
                return modalOptionsCache.musicTypes;
            }

            if (!modalOptionsCachePromise.musicTypes) {
                modalOptionsCachePromise.musicTypes = fetchJson(process.env.REACT_APP_API_URL_BASE + '/music_types/')
                    .then((data) => {
                        modalOptionsCache.musicTypes = data;
                        return data;
                    })
                    .finally(() => {
                        modalOptionsCachePromise.musicTypes = null;
                    });
            }

            return modalOptionsCachePromise.musicTypes;
        };

        const getCachedCustomers = async () => {
            if (modalOptionsCache.customers) {
                return modalOptionsCache.customers;
            }

            if (!modalOptionsCachePromise.customers) {
                modalOptionsCachePromise.customers = fetchJson(process.env.REACT_APP_API_URL_BASE + '/customers/')
                    .then((data) => {
                        modalOptionsCache.customers = data;
                        return data;
                    })
                    .finally(() => {
                        modalOptionsCachePromise.customers = null;
                    });
            }

            return modalOptionsCachePromise.customers;
        };

        let isMounted = true;

        const initializeModalData = async () => {
            setIsLoadingModalData(true);
            try {
                const userType = tokenUserType;
                if (userType < 4) {
                    setCustomerSelectDisabled(true);
                }

                const baseRequests = [
                    getCachedMusicTypes(),
                    getCachedCustomers(),
                ];

                if (!isNewClient) {
                    baseRequests.push(fetchJson(process.env.REACT_APP_API_URL_BASE + '/client_protocols/client_list/' + clientEdit.id));
                    baseRequests.push(fetchJson(process.env.REACT_APP_API_URL_BASE + '/customer_clients/client/' + clientEdit.id));
                }

                const results = await Promise.all(baseRequests);
                if (!isMounted) {
                    return;
                }

                const musicData = results[0];
                const customersData = results[1];
                const clientProtocolsData = !isNewClient ? results[2] : [];
                const customerClientData = !isNewClient ? results[3] : [];

                const musicOptions = musicData.map(item => ({ description: item.description, id: item.music_type_id }));
                setAllMusicTypes(musicOptions);

                const formattedCustomers = customersData.map(item => ({ customer_name: item.customer_name, id: item.customer_id }));
                setAllCustomers(formattedCustomers);

                const existingCustomerIdFromClient = Number(clientEdit?.customer_id ?? clientEdit?.customer);
                const customerIdFromJoin = Number(customerClientData?.[0]?.customer?.customer_id);
                const fallbackCustomerId = Number(formattedCustomers?.[0]?.id);
                const resolvedCustomerId = Number(
                    (isNewClient
                        ? existingCustomerIdFromClient
                        : (customerIdFromJoin || existingCustomerIdFromClient)
                    )
                    || tokenCustomerId
                    || fallbackCustomerId
                );

                const protocolsData = await getCachedProtocolsForCustomer(resolvedCustomerId);
                if (!isMounted) {
                    return;
                }

                setAllProtocols(normalizeProtocolOptions(protocolsData));

                if (!isNewClient) {
                    const formattedClientProtocols = clientProtocolsData.map(item => ({
                        name: item.protocol.name,
                        id: item.protocol.protocol_id,
                        personal_name: item.personal_name
                    }));
                    setClientProtocols(formattedClientProtocols);

                    if (customerClientData.length > 0) {
                        setClientInfo((previousClientInfo) => ({
                            ...previousClientInfo,
                            customer_name: customerClientData[0].customer.customer_name,
                            customer_id: customerClientData[0].customer.customer_id
                        }));
                    }
                }
            } catch (error) {
                console.error(error);
            } finally {
                if (isMounted) {
                    setIsLoadingModalData(false);
                }
            }
        };

        initializeModalData();

        return () => {
            isMounted = false;
        };
        }, [clientEdit?.id, clientEdit?.customer, clientEdit?.customer_id, isNewClient, tokenCustomerId, tokenUserType]);

    const apiPatchClientInfo = async (clientId, patchClientInfo) => {
        // console.log(patchClientInfo)
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/clients/client/' + clientId,
            {
                method: 'PATCH',
                headers: {
                    'machine':'2',
                    'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                    'content-type': 'application/json'
                },
                body: JSON.stringify(patchClientInfo),
            });
            if (!response.ok) {
                console.log('Failed to update the resource');
                console.log(response);
            }
            // Add client to selected customer
            // console.log('adding client to customer ' + patchClientInfo.customer_id)
            await addClientToCustomer(clientId)
    }

    const apiPostNewClient = async (patchClientInfo) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/clients/',
            {
                method: 'POST',
                headers: {
                    'machine':'2',
                    'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                    'content-type': 'application/json'
                },
                body: JSON.stringify(patchClientInfo),
            });
        const data = await response.json();
        if (response.ok) {
           // (data.client_id)
            await updateClientProtocols(data.client_id, clientProtocols, addedClientProtocols, removedClientProtocols);
            console.log('adding client to customer ' + tokenPayload.customer)
            await addClientToCustomer(data.client_id)

        }
        else {
            console.log('Failed to update the resource');
            console.log(response);
        }
        return true;
    }

    const addClientToCustomer = async (client_id) => {
        const add_body = {
            "client": parseInt(client_id),
            "customer": clientInfo.customer_id
        };
        // console.log(clientInfo)
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/customer_clients/',
            {
                method: 'POST',
                headers: {
                    'machine':'2',
                    'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                    'content-type': 'application/json'
                },
                body: JSON.stringify(add_body),
            });
        if (!response.ok) {
            console.log('Failed to add the client to the customer');
            console.log(response);
        }
    }

    const updateClientProtocols = async (clientId, clientProtocols, addedClientProtocols, removedClientProtocols) => {
        if (removedClientProtocols.length){
            // remove protocols from the client
            for (const protocol of removedClientProtocols){
                const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/client_protocols/client/' + clientId + '/protocol/' + protocol["id"],
                    {
                        method: 'DELETE',
                        headers: {
                            'machine':'2',
                            'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                            'content-type': 'application/json'
                        },
                        body: '',
                    });
                if (!response.ok) {
                console.log('Failed to remove the protocol from the client');
                console.log(response);
                }
            }
        }
        if (addedClientProtocols.length){
            // Add the new protocols to the client
            for (const protocol of addedClientProtocols){
                const add_body = {
                    "client": clientId,
                    "protocol": protocol['id']
                };
                const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/client_protocols/',
                    {
                        method: 'POST',
                        headers: {
                            'machine':'2',
                            'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                            'content-type': 'application/json'
                        },
                        body: JSON.stringify(add_body),
                    });

                    if (!response.ok) {
                    console.log('Failed to add the protocol to the client');
                    console.log(response);
                    }
            }
        }
        if (clientInfo.personalize_protocols) {
            // go through the client protocols and update the personalized name
            for (const protocol of clientProtocols){
                const add_body = {
                    "client": clientId,
                    "protocol": protocol['id'],
                    "personal_name": protocol['personal_name']
                };
                if (add_body.personal_name !== null) {
                    const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/client_protocols/client/' + clientId + '/protocol/' + protocol['id'],
                        {
                            method: 'PATCH',
                            headers: {
                                'machine':'2',
                                'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                                'content-type': 'application/json'
                            },
                            body: JSON.stringify(add_body),
                        });
                    if (!response.ok) {
                        console.log('Failed to update the personalized name');
                        console.log(response);
                    }
                }
            }
        }
    }

    const validateForm = async () => {
        if (clientInfo.first_name && clientInfo.last_name && clientInfo.mobile && clientInfo.pin){
            setErrors("")
            const patchClientInfo = {...clientInfo}
            const clientId = patchClientInfo.id
            console.log(patchClientInfo)
            let keysToRemove = ['client_id', 'created_on', 'last_updated', 'old_client_id']
            if (!Number.isInteger(patchClientInfo.music_type)) {
                keysToRemove = keysToRemove.concat('music_type')
            }
            keysToRemove.map((e) => delete patchClientInfo[e])
            setClientInfo(patchClientInfo)
            if (isNewClient) {
                await apiPostNewClient(patchClientInfo)
            }
            else {
                await apiPatchClientInfo(clientId, patchClientInfo)
                await updateClientProtocols(clientId, clientProtocols, addedClientProtocols, removedClientProtocols)
            }

            return true;
        }
        else {
            let errorFields = [];
            for(const [key, value] of Object.entries(clientInfo)) {
                if(!value){
                    errorFields.push(key);
                }
            }
            setErrors(errorFields.join(", "));
            return false;
        }
    }

    const checkInAdd = (checkProtocol) => {
        return addedClientProtocols.some(protocol => protocol.id === checkProtocol.id)
    }

    const checkInRemove = (checkProtocol) => {
        return removedClientProtocols.some(protocol => protocol.id === checkProtocol.id)
    }

    const checkInClient = (checkProtocol) => {
        return clientProtocols.some(protocol => protocol.id === checkProtocol.id)
    }

    const handleRemove = (selectedList, removedItem) => {
        if (checkInClient(removedItem) && !checkInRemove(removedItem)){
            // add to removedProtocol array
            setRemovedClientProtocols((prevRemovedValues) => [...prevRemovedValues, removedItem])
        }
        if (checkInAdd(removedItem)){
            // remove from addedProtocol array
            const tempAdd = addedClientProtocols
            const idx = tempAdd.findIndex(item => item.id === removedItem.id)
            tempAdd.splice(idx,1);
            setAddedClientProtocols(tempAdd)
        }
    }

    const handleSelect = (selectedList, selectedItem) => {
        if (!checkInClient(selectedItem) && !checkInAdd(selectedItem)){
            setAddedClientProtocols((prevAddedValues) => [...prevAddedValues, selectedItem])
        }
        if (checkInRemove(selectedItem)){
            // remove from addedProtocol array
            const tempRem = removedClientProtocols
            const idx = tempRem.findIndex(item => item.id === selectedItem.id)
            tempRem.splice(idx,1);
            setRemovedClientProtocols(tempRem)
        }
    }

    const handleChange = (e) => {
        setClientInfo((previousClientInfo) => ({
            ...previousClientInfo,
            [e.target.name]: e.target.value
        }))
    }

    const formatPhoneDisplay = (value) => {
        const digits = String(value ?? '').replace(/\D/g, '').slice(0, 10);
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    const handleMobileChange = (e) => {
        const rawDigits = e.target.value.replace(/\D/g, '').slice(0, 10);
        setClientInfo((previousClientInfo) => ({
            ...previousClientInfo,
            mobile: rawDigits
        }))
    }

    const handlePinChange = (e) => {
        const pinDigits = e.target.value.replace(/\D/g, '').slice(0, 8);
        setClientInfo((previousClientInfo) => ({
            ...previousClientInfo,
            pin: pinDigits
        }))
    }

    const handleChangeInt = (e) => {
        setClientInfo((previousClientInfo) => ({
            ...previousClientInfo,
            [e.target.name]: parseInt(e.target.value)
        }))
    }

    const handleMusicChange = (e) => {
        const selectedMusicId = Number(e.target.value);
        const selectedMusic = allMusicTypes.find((musicType) => Number(musicType.id) === selectedMusicId);
        setClientInfo((previousClientInfo) => ({
            ...previousClientInfo,
            'music_description': selectedMusic?.description ?? '',
            'music_type': selectedMusicId
        }))
    }

     const handlePersonalNameChange = (e) => {
        // console.log(e)
        setSelectedPersonalizedProtocolIndex(e.target.selectedIndex)

    }

    const handleChangeSelectedPersonalizedProtocol = (e) => {
        // console.log(e.target.value)
        // console.log(selectedPersonalizedProtocolIndex)
        // console.log(clientProtocols[selectedPersonalizedProtocolIndex])
        setClientProtocols(prev =>
        prev.map((item, idx) =>
            idx === selectedPersonalizedProtocolIndex
                ? { ...item, personal_name: e.target.value }
                : item
            )
        );
    }

    const handleCustomerChange = async (e) => {
        const selectedCustomerId = Number(e.target.value);
        const selectedCustomer = allCustomers.find((customer) => Number(customer.id) === selectedCustomerId);
        let customerProtocols = [];

        try {
            const cacheKey = `customer-protocols-${selectedCustomerId}`;
            if (modalOptionsCache.protocolsByKey[cacheKey]) {
                customerProtocols = modalOptionsCache.protocolsByKey[cacheKey];
            } else {
                const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/customer_protocols/customer_list/' + selectedCustomerId, {
                    method: 'GET',
                    headers: {
                        'machine':'2',
                        'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                    },
                });

                if (!response.ok) {
                    throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
                }

                customerProtocols = await response.json();
                modalOptionsCache.protocolsByKey[cacheKey] = customerProtocols;
            }
        } catch (error) {
            console.error(error);
        }

        setAllProtocols(normalizeProtocolOptions(customerProtocols));
        setClientProtocols([]);
        setAddedClientProtocols([]);
        setRemovedClientProtocols([]);
        setSelectedPersonalizedProtocolIndex(0);

        setClientInfo((previousClientInfo) => ({
            ...previousClientInfo,
            "customer_name": selectedCustomer?.customer_name ?? '',
            "customer_id": selectedCustomerId
        }))
    }

    const handleCheckChange = (e) => {
        // console.log(e.target.name)
        // console.log(e.target.checked)
        setClientInfo((previousClientInfo) => ({
            ...previousClientInfo,
            [e.target.name]: e.target.checked
        }))
    }

    const handleSubmit = async (e) => {
        // console.log(clientInfo)
        e.preventDefault();
        setSavingData(true);
        var isValidated = await validateForm()
        if (!isValidated) return;

        onSubmit(clientInfo);
        closeModal();
    }



    const handleCancel = (e) => {
        e.preventDefault();
        closeModal();
    }

    const handleOpenTreatmentHistory = (e) => {
        e.preventDefault();
        if (typeof onOpenTreatmentHistory === 'function') {
            onOpenTreatmentHistory(clientInfo);
        }
    }

    const handleOpenTreatmentNotes = (e) => {
        e.preventDefault();
        if (typeof onOpenTreatmentNotes === 'function') {
            onOpenTreatmentNotes(clientInfo);
        }
    }

    return (
        <div className='modal-container client-data-modal-overlay'>
            <div className='modal-edit client-data-modal'>
        <button className='modal-close-btn' onClick={handleCancel} aria-label='Close client data modal'>X</button>
        <form>
        {isLoadingModalData ? (
            <div className='client-skeleton-grid'>
                <section className='client-card'>
                    <div className='skeleton-line skeleton-title'></div>
                    <div className='skeleton-line skeleton-id-row'></div>
                    <div className='skeleton-input-row'>
                        <div className='skeleton-line skeleton-input'></div>
                        <div className='skeleton-line skeleton-input'></div>
                    </div>
                    <div className='skeleton-input-row'>
                        <div className='skeleton-line skeleton-input short'></div>
                        <div className='skeleton-line skeleton-input short'></div>
                        <div className='skeleton-line skeleton-input'></div>
                    </div>
                    <div className='skeleton-line skeleton-input'></div>
                </section>
                <section className='client-card'>
                    <div className='skeleton-line skeleton-title'></div>
                    <div className='skeleton-input-row'>
                        <div className='skeleton-line skeleton-input'></div>
                        <div className='skeleton-line skeleton-input'></div>
                    </div>
                    <div className='skeleton-line skeleton-input'></div>
                    <div className='skeleton-line skeleton-input'></div>
                    <div className='skeleton-line skeleton-input tall'></div>
                </section>
                {!isNewClient && (
                    <section className='client-card'>
                        <div className='skeleton-line skeleton-title short'></div>
                        <div className='skeleton-line skeleton-input short'></div>
                        <div className='skeleton-line skeleton-input short'></div>
                    </section>
                )}
            </div>
        ) : (
        <div className='client-card-grid'>
            <section className='client-card'>
                <div className='client-card-header'>
                    <h3>Client Info</h3>
                </div>
                <div className='client-fields-grid two-col'>
                    <div className='wide id-row'>
                        <label htmlFor='client_id' className='id-value'>ID: {isNewClient ? 'New Client' : (clientInfo.id ?? '')}</label>
                        <div className='id-active-toggle'>
                            <input id='is_active' name="is_active" type="checkbox" value={clientInfo.is_active} onChange={handleCheckChange} checked={clientInfo.is_active} disabled={isReadOnlyMode}/>
                            <label htmlFor='is_active'>Active</label>
                        </div>
                    </div>
                    <div className='form-group'>
                        <label htmlFor='first_name'>First*</label>
                        <input name="first_name" value={clientInfo.first_name} onChange={handleChange} readOnly={isReadOnlyMode}/>
                    </div>
                    <div className='form-group'>
                        <label htmlFor='last_name'>Last*</label>
                        <input name="last_name" value={clientInfo.last_name} onChange={handleChange} readOnly={isReadOnlyMode}/>
                    </div>
                    <div className='contact-row wide'>
                        <div className='form-group mobile-field'>
                            <label htmlFor='mobile'>Mobile*</label>
                            <input
                                name="mobile"
                                value={formatPhoneDisplay(clientInfo.mobile)}
                                onChange={handleMobileChange}
                                inputMode='numeric'
                                autoComplete='tel-national'
                                readOnly={isReadOnlyMode}
                            />
                        </div>
                        <div className='form-group pin-field'>
                            <label htmlFor='pin'>Pin*</label>
                            <input
                                name="pin"
                                value={clientInfo.pin}
                                onChange={handlePinChange}
                                inputMode='numeric'
                                maxLength={8}
                                readOnly={isReadOnlyMode}
                            />
                        </div>
                        <div className='form-group email-field'>
                            <label htmlFor='email'>Email</label>
                            <input name="email" value={clientInfo.email} onChange={handleChange} readOnly={isReadOnlyMode}/>
                        </div>
                    </div>
                    <div className='form-group wide'>
                        <label htmlFor='customer'>Customer</label>
                        <select
                            name="customer"
                            value={clientInfo.customer_id ?? ''}
                            onChange={handleCustomerChange}
                            disabled={customerSelectDisabled || isReadOnlyMode}
                            >
                            {allCustomers.map ((customer) => (
                                <option key={customer.id} value={customer.id} customer_id={customer.id}>
                                    {customer.customer_name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </section>

            <section className='client-card'>
                <h3>Treatment Settings</h3>
                <div className='client-fields-grid two-col'>
                    <div className='form-group'>
                        <label htmlFor='dosage'>Dosage</label>
                        <input type="number" name="dosage" value={clientInfo.dosage} onChange={handleChangeInt} readOnly={isReadOnlyMode}/>
                    </div>
                    <div className='form-group'>
                        <label htmlFor='music_type'>Music Preference</label>
                        <select name="music_description" value={clientInfo.music_type ?? ''} onChange={handleMusicChange} disabled={isReadOnlyMode}>
                            {allMusicTypes.map ((music_type) => (
                                <option key={music_type.id} value={music_type.id}>
                                    {music_type.description}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className='form-group toggle-field'>
                        <input id='steady_only' name="steady_only" type="checkbox" value={clientInfo.steady_only} onChange={handleCheckChange} checked={clientInfo.steady_only} disabled={isReadOnlyMode}/>
                        <label htmlFor='steady_only'>Steady Only</label>
                    </div>
                    <div className='form-group toggle-field'>
                        <input id='play_audio_frequency' name="play_audio_frequency" type="checkbox" value={clientInfo.play_audio_frequency} onChange={handleCheckChange} checked={clientInfo.play_audio_frequency} disabled={isReadOnlyMode}/>
                        <label htmlFor='play_audio_frequency'>Play Audio Frequency</label>
                    </div>
                    <div className='form-group wide'>
                        <label htmlFor='protocols'>Protocols Assigned</label>
                        <div
                            ref={protocolSelectRef}
                            className={`client-protocol-select-wrapper ${protocolDropdownLayout.openUpward ? 'opens-upward' : 'opens-downward'}`}
                            style={{ '--protocol-dropdown-max-height': `${protocolDropdownLayout.maxHeight}px` }}
                            onClick={updateProtocolDropdownLayout}
                            onFocus={updateProtocolDropdownLayout}
                        >
                            <Multiselect
                                className="customer-protocol-select"
                                displayValue="name"
                                onKeyPressFn={function noRefCheck(){}}
                                onRemove={handleRemove}
                                onSearch={function noRefCheck(){}}
                                onSelect={handleSelect}
                                options={allProtocols}
                                selectedValues={clientProtocols}
                                disable={isReadOnlyMode}
                            />
                        </div>
                    </div>
                    <div className='form-group toggle-field wide'>
                        <input id='personalize_protocols' name="personalize_protocols" type="checkbox" value={clientInfo.personalize_protocols} onChange={handleCheckChange} checked={clientInfo.personalize_protocols} disabled={isReadOnlyMode}/>
                        <label htmlFor='personalize_protocols'>Personalize Protocols</label>
                    </div>
                    { clientInfo.personalize_protocols &&
                    <>
                        <div className='form-group'>
                            <label htmlFor='selected_protocol'>Select Protocol</label>
                            <select name="personal_name" onChange={handlePersonalNameChange} disabled={isReadOnlyMode}>
                                {clientProtocols.map ((protocol) => (
                                    <option key={protocol.id} value={protocol.id}>
                                        {protocol.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className='form-group'>
                            <label htmlFor='personal_name'>Personalized Name</label>
                            <input name="personal_name" value={clientProtocols[selectedPersonalizedProtocolIndex]?.personal_name ?? ''} onChange={handleChangeSelectedPersonalizedProtocol} readOnly={isReadOnlyMode}/>
                        </div>
                    </>
                    }
                </div>
            </section>

            {!isNewClient && (
                <section className='client-card'>
                    <h3>Treatments</h3>
                    <div className='treatments-links'>
                        <button type='button' className='treatments-link' onClick={handleOpenTreatmentHistory}>Treatment History</button>
                        <button type='button' className='treatments-link' onClick={handleOpenTreatmentNotes}>Notes</button>
                    </div>
                </section>
            )}

        </div>
        )}

            {!isLoadingModalData && !isReadOnlyMode && errors && <div className='error'>{`Please include: ${errors}`}</div>}
            {!isLoadingModalData && !isReadOnlyMode && savingData && <div className='error'>{`Saving Client Data - Please Wait.....`}</div>}
            {!isLoadingModalData && (
                <div className='client-actions-row'>
                    <button type='button' className='btn btn-cancel' onClick={handleCancel}>{isReadOnlyMode ? 'Close' : 'Cancel'}</button>
                    {!isReadOnlyMode && <button type='submit' className='btn' onClick={handleSubmit}>Save</button>}
                </div>
            )}
        </form>
      </div>
    </div>
  )
}

export default ModalWide


